/**
 * DocumentRow — Documents Library list row
 *
 * 32×40 file thumbnail + name + meta (size · uploadedAt · MonoID) +
 * tag chips + status chip + eye/download/more IconButtons.
 *
 * Spec: /tmp/design-pass/borrower/specs/sign-and-documents.md (Screen 2 row pattern)
 *
 * Rules:
 * - Glass components only (rule 11b)
 * - sx + theme tokens, alpha() / baseColors for tint (rule 11c)
 * - All copy via t() (rule 13)
 * - 44px touch target on every IconButton (portals.md)
 */

import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  alpha,
  Box,
  baseColors,
  GlassChip,
  GlassIconButton,
  GlassTooltip,
  Stack,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';

export type DocumentRowStatus = 'verified' | 'pending-review' | 'expired';

export interface DocumentRowProps {
  id: string;
  name: string;
  /** Human-readable size, already formatted (e.g. "2.4 MB"). */
  size: string;
  /** Already-formatted upload date (e.g. "Apr 14"). */
  uploadedAt: string;
  /** MonoID (loan reference, first two segments). */
  monoId?: string;
  type: 'pdf' | 'xlsx' | 'doc' | 'image' | 'other';
  status: DocumentRowStatus;
  tags: string[];
  onPreview: () => void;
  onDownload?: () => void;
  onMore?: () => void;
}

function FileTypeThumb({ type }: { type: DocumentRowProps['type'] }) {
  const label =
    type === 'pdf' ? 'PDF' : type === 'xlsx' ? 'XLS' : type === 'doc' ? 'DOC' : type === 'image' ? 'IMG' : 'FILE';
  const tone: 'mint' | 'amber' | 'neutral' = type === 'pdf' ? 'mint' : type === 'xlsx' ? 'amber' : 'neutral';
  const bgColor =
    tone === 'mint'
      ? alpha(baseColors.teal[400], 0.16)
      : tone === 'amber'
        ? alpha(baseColors.amber[500], 0.16)
        : alpha(baseColors.neutral[500], 0.12);
  const labelColor = tone === 'mint' ? 'success.main' : tone === 'amber' ? 'warning.main' : 'text.secondary';
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 32,
        height: 40,
        borderRadius: 0.75,
        bgcolor: bgColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Typography variant="caption" sx={{ color: labelColor, fontWeight: 700, letterSpacing: '0.08em' }}>
        {label}
      </Typography>
    </Box>
  );
}

function statusChipColor(status: DocumentRowStatus): 'success' | 'warning' | 'error' {
  if (status === 'verified') return 'success';
  if (status === 'pending-review') return 'warning';
  return 'error';
}

export function DocumentRow(props: DocumentRowProps) {
  const { t } = useI18n();
  const statusKey =
    props.status === 'verified'
      ? 'consumer.documents.statusVerified'
      : props.status === 'pending-review'
        ? 'consumer.documents.statusPendingReview'
        : 'consumer.documents.statusExpired';

  const metaParts: string[] = [props.size, props.uploadedAt];
  if (props.monoId) metaParts.push(props.monoId);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { md: 'center' },
        gap: 1.5,
        py: 1.5,
        px: 2,
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 0 },
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <FileTypeThumb type={props.type} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word', mb: 0.25 }}>
          {props.name}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {metaParts.join(' • ')}
        </Typography>
      </Box>

      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
        {props.tags.map((tag) => (
          <GlassChip key={tag} label={tag} size="small" variant="outlined" />
        ))}
      </Stack>

      <GlassChip label={t(statusKey)} size="small" color={statusChipColor(props.status)} />

      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
        <GlassTooltip title={t('consumer.documents.preview')}>
          <GlassIconButton size="medium" onClick={props.onPreview} aria-label={t('consumer.documents.preview')}>
            <VisibilityIcon />
          </GlassIconButton>
        </GlassTooltip>
        {props.onDownload && (
          <GlassTooltip title={t('consumer.documents.download')}>
            <GlassIconButton size="medium" onClick={props.onDownload} aria-label={t('consumer.documents.download')}>
              <DownloadIcon />
            </GlassIconButton>
          </GlassTooltip>
        )}
        {props.onMore && (
          <GlassTooltip title={t('common.more')}>
            <GlassIconButton size="medium" onClick={props.onMore} aria-label={t('common.more')}>
              <MoreVertIcon />
            </GlassIconButton>
          </GlassTooltip>
        )}
      </Stack>
    </Box>
  );
}

export default DocumentRow;
